import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { ReactNode } from "react";
import "./overrides.css";

interface PropertiesListLeaf {
    term: ReactNode;
    description?: ReactNode;
    render?: ReactNode;
}

type PropertiesListCollection = Array<PropertiesListLeaf>;

interface PropertiesListConfigLeaf<T extends object> {
    term: string | ((data: T) => string);
    description?: string | ((data: T) => ReactNode);
    render?: ReactNode | ((data: T) => ReactNode);
}

type PropertiesListConfig<T extends object> = Array<PropertiesListConfigLeaf<T>>;

const createPropertiesList = <T extends object>(
    data: T | undefined,
    config: PropertiesListConfig<T>
): PropertiesListCollection => {
    if (!data) {
        return [];
    }

    return config.map(({ term, description, render }) => {
        return {
            term: typeof term === "function" ? term(data) : term,
            description: typeof description === "function" ? description(data) : description,
            render: typeof render === "function" ? render(data) : render,
        };
    });
};

interface PropertiesListProps {
    list: PropertiesListCollection;
}

const Component = ({ list }: PropertiesListProps) => {
    const colConfig: ColumnsType = [
        {
            dataIndex: "term",
            key: "term",
        },
        {
            dataIndex: "description",
            key: "description",
            render: (value, record) => {
                return record.render ?? value;
            },
        },
    ];

    return (
        <Table
            size={"small"}
            tableLayout={"fixed"}
            showHeader={false}
            pagination={false}
            rowHoverable={true}
            bordered={false}
            columns={colConfig}
            dataSource={list}
            className={"p-3"}
        />
    );
};

export const PropertiesList = Object.assign(Component, { create: createPropertiesList });
